import { Form, Input, message, Tabs } from 'antd';
import { ACTION_TITLE } from 'constants/constant';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import { LanguageInterface } from 'content/global-settings/types/globalSettingType';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { genreApiService } from 'service/api/genreApiService';
import * as Component from '../../../../shared/components';
import { I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
import { genreService } from '../services/genreService';

const GenreEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [i18nName, setI18nName] = useState<I18nFieldData>({});
  const [writeLanguages, setWriteLanguages] = useState<LanguageInterface[]>([]);

  useEffect(() => {
    // Fetch write languages
    const fetchWriteLanguages = async () => {
      try {
        const response = await globalSettingService.fetchLanguages(
          new URLSearchParams({
            type: 'write',
            page_size: '100',
            order_by: 'order',
            order_direction: 'asc',
          })
        );
        setWriteLanguages(response.results);
      } catch (error) {
        console.error('Failed to fetch write languages:', error);
      }
    };

    fetchWriteLanguages();

    setIsLoading(true);
    genreApiService.fetchById(Number(id)).then(data => {
      // Initialize i18n data
      setI18nName(data.i18n_name || {});
      form.setFieldsValue({
        ...data,
        // Set legacy name from i18n_name.ko if available
        name: data.name || '',
      });
      setIsLoading(false);
    });
  }, [id, form]);

  return (
    <Component.BasePageComponent>
      {isLoading ? (
        <Component.LoadingSpinnerComponent />
      ) : (
        <div>
          <div className="people-details-page">
            <h1>Genre</h1>
            <Form form={form} layout="vertical" className="people-details-form">
              <Form.Item label="Genre Name" required>
                <Tabs
                  type="card"
                  items={writeLanguages.map(lang => ({
                    key: lang.rfc_code.toLowerCase(),
                    label: `Title ${lang.rfc_code}`,
                    children: (
                      <Input
                        value={(i18nName as any)[lang.rfc_code.toLowerCase()] || ''}
                        onChange={e => {
                          setI18nName({
                            ...i18nName,
                            [lang.rfc_code.toLowerCase()]: e.target.value,
                          } as I18nFieldData);
                        }}
                        placeholder="Enter genre name"
                      />
                    ),
                  }))}
                />
              </Form.Item>
              <Form.Item
                style={{ marginTop: '10px' }}
                name="slug"
                label="Slug"
                rules={[{ required: true, message: 'please enter slug' }]}
              >
                <Input onChange={e => (e.target.value = e.target.value.toLowerCase())} />
              </Form.Item>
            </Form>
          </div>
          <div className="people-detail-update-button">
            <Component.ColorButtonComponent
              text={ACTION_TITLE.EDIT}
              onClick={async () => {
                const loadingMessage = message.loading('Updating...', 0);
                try {
                  // Validate form first
                  await form.validateFields();

                  // Get current form values
                  const formValues = form.getFieldsValue();

                  // Prepare complete genre data with i18n fields
                  const genreData = {
                    slug: formValues.slug || '',
                    i18n_name: i18nName || {},
                    // Legacy field: use ko value from i18n_name
                    name: i18nName?.ko?.toLowerCase() || '',
                  };

                  // Pass data directly to genreService
                  await genreService.updateGenre(Number(id), form, genreData);
                  loadingMessage();
                  navigate('/genre');
                } catch (error) {
                  loadingMessage();
                  console.error('Error updating genre:', error);
                }
              }}
            />
          </div>
        </div>
      )}
    </Component.BasePageComponent>
  );
};

export default observer(GenreEdit);
